import { useState } from "react";
import { VerifyTaskCompletion } from "../Services/TaskAssistantService";

const pick = (obj, key) => obj?.[key] ?? obj?.[key.charAt(0).toLowerCase() + key.slice(1)];

const SEVERITY = {
	1: { label: "Low", cls: "bg-slate-100 text-slate-700 ring-slate-300" },
	2: { label: "Medium", cls: "bg-amber-50 text-amber-800 ring-amber-300" },
	3: { label: "High", cls: "bg-orange-50 text-orange-800 ring-orange-300" },
	4: { label: "Critical", cls: "bg-red-50 text-red-800 ring-red-300" },
};

const VERDICT = {
	"Genuinely Complete": "bg-emerald-50 text-emerald-800 ring-emerald-300",
	"Not Supported": "bg-red-50 text-red-800 ring-red-300",
	Unclear: "bg-amber-50 text-amber-800 ring-amber-300",
};

const SUGGESTIONS = [
	"Show all high severity tasks",
	"List tasks with status Invalid",
	"Is the login task genuinely complete?",
	"Find tasks related to payment gateway",
];

function Badge({ className, children }) {
	return (
		<span
			className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${className}`}
		>
			{children}
		</span>
	);
}

const fmt = (d) => (d ? new Date(d).toLocaleDateString() : null);

function Field({ label, value }) {
	if (!value) return null;
	return (
		<div className="flex gap-2">
			<dt className="text-slate-500">{label}</dt>
			<dd className="font-medium text-slate-900">{value}</dd>
		</div>
	);
}

function TaskCard({ task }) {
	const sev = SEVERITY[pick(task, "Severity")];
	const verdict = pick(task, "VerificationVerdict");
	const reason = pick(task, "VerificationReason");
	const remark = pick(task, "Remark");
	const lastBy = pick(task, "LastUpdatedBy");
	const lastAt = fmt(pick(task, "LastUpdatedAt"));

	return (
		<li className="rounded-lg border border-slate-200 bg-white p-4 sm:p-5">
			<div className="flex flex-wrap items-center gap-2">
				<span className="font-mono text-sm font-semibold text-slate-900">
					{pick(task, "TaskNumber") || `#${pick(task, "TaskID")}`}
				</span>
				<Badge className="bg-indigo-50 text-indigo-800 ring-indigo-200">
					{pick(task, "StatusName") || "No status"}
				</Badge>
				{sev && <Badge className={sev.cls}>{sev.label}</Badge>}
				{verdict && <Badge className={VERDICT[verdict] || VERDICT.Unclear}>{verdict}</Badge>}
			</div>

			<p className="mt-3 text-sm leading-relaxed text-slate-700">{pick(task, "Description")}</p>

			<dl className="mt-3 grid grid-cols-1 gap-x-8 gap-y-1 text-sm sm:grid-cols-2">
				<Field label="Project" value={pick(task, "ProjectName")} />
				<Field label="Assigned to" value={pick(task, "AssignedToName")} />
				<Field label="Assigned by" value={pick(task, "AssignedByName")} />
				<Field label="Created by" value={pick(task, "CreatedByName")} />
				<Field label="Created" value={fmt(pick(task, "CreatedAt"))} />
				<Field label="Estimated" value={fmt(pick(task, "EstimatedComplete"))} />
				<Field label="Closed" value={fmt(pick(task, "ClosedDate"))} />
			</dl>

			{(remark || lastBy) && (
				<div className="mt-4 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
					{remark && <p>{remark}</p>}
					{lastBy && (
						<p className="mt-1 text-xs text-slate-500">
							Last updated by {lastBy}
							{lastAt && ` on ${lastAt}`}
						</p>
					)}
				</div>
			)}

			{reason && (
				<div className="mt-3 rounded-md border-l-4 border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700">
					{reason}
				</div>
			)}
		</li>
	);
}

/* ------------------------------------------------------------------ */
/* Screen                                                             */
/* ------------------------------------------------------------------ */
export default function TaskAssistant() {
	const [query, setQuery] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [response, setResponse] = useState(null);

	const run = async (text) => {
		const q = (text ?? query).trim();
		if (!q || loading) return;

		setQuery(q);
		setLoading(true);
		setError("");
		setResponse(null);

		try {
			const data = await VerifyTaskCompletion(q);
			setResponse(data);
		} catch (e) {
			setError(e.message);
		} finally {
			setLoading(false);
		}
	};

	const result = pick(response, "Result");
	const answer = pick(result, "Answer");
	const tasks = pick(result, "Tasks") || [];
	const rawAI = pick(response, "RawAIResponse");

	return (
		<div className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6">
			<div className="mx-auto max-w-3xl">
				<h1 className="text-2xl font-semibold text-slate-900">Task assistant</h1>
				<p className="mt-1 text-sm text-slate-600">
					Ask about tasks in plain language. Answers come only from your task data.
				</p>

				{/* Query box */}
				<div className="mt-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
					<label htmlFor="query" className="sr-only">
						Your question
					</label>
					<textarea
						id="query"
						rows={3}
						maxLength={1000}
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) run();
						}}
						placeholder="e.g. Is the invoice task genuinely complete?"
						className="w-full resize-none rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
					/>
					<div className="mt-3 flex flex-wrap items-center justify-between gap-3">
						<span className="text-xs text-slate-500">{query.length}/1000 · Ctrl+Enter to send</span>
						<button
							type="button"
							onClick={() => run()}
							disabled={loading || !query.trim()}
							className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
						>
							{loading && (
								<svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
									<circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
									<path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
								</svg>
							)}
							{loading ? "Thinking…" : "Ask"}
						</button>
					</div>
				</div>

				{/* Suggestions (only before first answer) */}
				{!response && !loading && !error && (
					<div className="mt-4 flex flex-wrap gap-2">
						{SUGGESTIONS.map((s) => (
							<button
								key={s}
								type="button"
								onClick={() => run(s)}
								className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:border-indigo-400 hover:text-indigo-700"
							>
								{s}
							</button>
						))}
					</div>
				)}

				{/* Loading note */}
				{loading && (
					<p className="mt-6 text-sm text-slate-600">
						The model is reading your tasks. This can take a minute on large datasets.
					</p>
				)}

				{/* Error */}
				{error && (
					<div role="alert" className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
						{error}
					</div>
				)}

				{/* Result */}
				{response && (
					<section className="mt-6 space-y-5" aria-live="polite">
						{result ? (
							<>
								<div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 sm:p-5">
									<h2 className="text-sm font-semibold text-indigo-900">Answer</h2>
									<p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-indigo-950">
										{answer}
									</p>
								</div>

								{tasks.length > 0 ? (
									<div>
										<h2 className="mb-3 text-sm font-semibold text-slate-900">
											Matching tasks ({tasks.length})
										</h2>
										<ul className="space-y-3">
											{tasks.map((t, i) => (
												<TaskCard key={pick(t, "TaskID") ?? i} task={t} />
											))}
										</ul>
									</div>
								) : (
									<p className="text-sm text-slate-500">No tasks matched this question.</p>
								)}
							</>
						) : (
							<div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
								<p className="text-sm text-amber-900">
									{pick(response, "Message") || "The response could not be read as structured data."}
								</p>
								{rawAI && (
									<pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap rounded bg-white p-3 text-xs text-slate-700">
										{rawAI}
									</pre>
								)}
							</div>
						)}
					</section>
				)}
			</div>
		</div>
	);
}
